import { Box, Text } from "@chakra-ui/layout";
import {
  Center,
  Wrap,
  Tooltip,
  VStack,
  Input,
  Skeleton,
} from "@chakra-ui/react";
import React from "react";
import type { UseFormReturn } from "react-hook-form";
import SportIconsFactory from "../../../components/icons/SportIconsFactory";
import type { ISportsListQuery } from "../../../lib/api/sdk";
import { useSportsListQuery } from "../../../lib/api/sdk";
import { useLocales } from "src/contexts/LocalesContext";

export interface ISportsRadioInputProps {
  FormController: UseFormReturn<any>;
  name: any;
}

const Label = ({ sport, title }: any) => {
  return (
    <Tooltip label={title} openDelay={500}>
      <Box as="span" maxW="full">
        <SportIconsFactory id={sport?.avatar} />
      </Box>
    </Tooltip>
  );
};

const Sport = ({
  sport,
  value = [],
  onChange,
  maxOptions,
  loading,
  colorScheme,
}: {
  sport: ISportsListQuery["sport"][0],
  value: string[],
  onChange: (value: any) => void,
  maxOptions?: number,
  loading?: boolean,
  colorScheme: string,
}) => {
  const { getTranslations } = useLocales();
  const sportTranslated = getTranslations(sport.sport_translations);

  const isActive = !loading && value.includes(sport?.id);

  const handleClick = () => {
    if (onChange) {
      const updatedValue = maxOptions === 1
        ? [sport?.id]
        : isActive
          ? value.filter((id: any) => id !== sport?.id)
          : [...value, sport?.id];
      onChange(updatedValue);
    }
  };

  return (
    <VStack
      flex={1}
      minH="65px"
      id="parent"
      sx={{
        "&:last-child > *": {
          borderRightWidth: "1px",
        },
      }}
    >
      <Center
        h="65px"
        minW={["65px", "full"]}
        maxW={["65px", "full"]}
        flex={1}
        className={isActive ? "active" : ""}
        onClick={handleClick}
        cursor="pointer"
        borderWidth="1px"
        borderRightWidth={0}
        sx={{
          "&.active": {
            bg: `${colorScheme}.500`,
            color: "white",
            borderColor: `${colorScheme}.500`,
          },
        }}
        _focus={{
          boxShadow: "outline",
        }}
        py={1.5}
        px={2.5}
      >
        <Label sport={sport} title={sportTranslated?.title} />
      </Center>
      <Skeleton isLoaded={!loading}>
        <Text whiteSpace="nowrap" fontSize="xs">
          {sportTranslated?.title}
        </Text>
      </Skeleton>
    </VStack>
  );
};

const SportsRadioInputs = ({ name, value, onChange, colorScheme, maxOptions = undefined }: {
  name: string,
  value: string[],
  onChange: (value: any) => void,
  maxOptions?: number,
  colorScheme: string,
}) => {
  const SportsListReq = useSportsListQuery();

  return (
    <>
      <Input
        pos="absolute"
        name={name}
        value={value}
        onChange={onChange}
        // eslint-disable-next-line no-inline-styles/no-inline-styles
        style={{ display: "none" }}
      />
      <Skeleton isLoaded={!SportsListReq.loading}>
        <Wrap
          direction="row"
          flex={1}
          w="full"
          spacing={0}
          justify="stretch"
        >
          {SportsListReq.data?.sport?.map(sport => (
            <Sport
              key={sport.id}
              sport={sport}
              value={value}
              onChange={onChange}
              maxOptions={maxOptions}
              colorScheme={colorScheme}
            />
          ))}
        </Wrap>
      </Skeleton>
    </>
  );
};

export default SportsRadioInputs;
